// ----------------------------------------------------------------------
// K9os App — International region data (Canada + Worldwide).
// ----------------------------------------------------------------------
// Powers the /dog-training-software/canada and
// /dog-training-software/worldwide landing pages. Kept separate from
// the U.S. states dataset so US-only copy stays clean.
// ----------------------------------------------------------------------

export const CANADA = {
  slug: 'canada',
  title: 'Canada',
  countryCode: 'CA',
  headline: 'Dog Training Software in Canada',
  subheadline: 'One App for Canadian Dog Trainers, Board-and-Train Facilities & Kennels',
  eyebrow: 'Canada · CA',
  areaLabel: 'coast-to-coast Canada',
  // Map config — geoMercator projection focused on the populated southern
  // band of Canada (roughly 45°N–58°N, where all 10 provinces + every
  // major city sit). Latitude ~57 pushes the empty Arctic islands out of
  // the viewport so the map isn't dominated by whitespace, and the higher
  // scale magnifies the coast-to-coast city ribbon.
  // Map config — geoMercator projection tightly focused on the populated
  // 45°N–54°N ribbon. The higher scale + southward-translated projection
  // origin push everything north of ~56°N above the top edge of the SVG,
  // so the empty Arctic / Baffin Island whitespace is truly cropped away.
  // Every city marker (Vancouver 49°N through Halifax 44°N) sits comfortably
  // in the middle band of the shorter 16/7 viewport.
  mapCenter: [-96, 51],
  mapScale: 900,
  mapAspect: '16/7',
  mapTranslate: [500, 130],
  // Province overlay temporarily disabled — reliable public topology
  // wasn't reachable from our CDN. City markers + country shape keep the
  // map interactive for now.
  // subdivisionsGeo: '…',
  mapCities: [
    { name: 'Toronto',      coords: [-79.3832, 43.6532], color: '#ff5b5b', labelDy: -12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Montreal',     coords: [-73.5673, 45.5017], color: '#ff5b5b', labelDy: -12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Vancouver',    coords: [-123.1207, 49.2827], color: '#ff5b5b', labelDy: -12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Calgary',      coords: [-114.0719, 51.0447], color: '#ff5b5b', labelDy: -12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Ottawa',       coords: [-75.6972, 45.4215], color: '#ffcf40', labelDy:   4, labelDx: -12, labelAnchor: 'end' },
    { name: 'Edmonton',     coords: [-113.4938, 53.5461], color: '#ffcf40', labelDy: -12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Winnipeg',     coords: [-97.1385, 49.8951], color: '#ffcf40', labelDy: -12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Quebec City',  coords: [-71.2080, 46.8139], color: '#ffcf40', labelDy: -12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Halifax',      coords: [-63.5752, 44.6488], color: '#ffcf40', labelDy: -12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Mississauga',  coords: [-79.6441, 43.5890], color: '#ffcf40', labelDy:  20, labelDx: -12, labelAnchor: 'end' },
  ],
  regions: [
    'Ontario', 'Quebec', 'British Columbia', 'Alberta',
    'Manitoba', 'Saskatchewan', 'Nova Scotia', 'New Brunswick',
    'Newfoundland & Labrador', 'Prince Edward Island',
  ],
  cities: [
    'Toronto', 'Montreal', 'Vancouver', 'Calgary', 'Ottawa',
    'Edmonton', 'Mississauga', 'Winnipeg', 'Quebec City', 'Hamilton',
  ],
  keywords: [
    'dog training software Canada',
    'dog trainer software Canada',
    'Canadian dog training business software',
    'dog trainer CRM Canada',
    'dog trainer POS Canada',
    'board and train software Canada',
    'kennel management software Canada',
    'all-in-one dog training system Canada',
  ],
  seoTitle: 'Dog Training Software in Canada — K9os App for Canadian Dog Trainers',
  seoDesc: 'K9os App is the all-in-one dog training software for Canadian dog trainers, board-and-train facilities, and kennels — Toronto, Montreal, Vancouver, Calgary and coast-to-coast. Online booking, CRM, POS, memberships, contracts, messaging, and marketing under one login.',
  intro: 'K9os App is the all-in-one dog training software built for dog trainers and dog training companies across Canada — from Toronto and Montreal to Vancouver, Calgary, and every province in between. Replace fragmented POS, scheduling, and CRM tools with one login for online booking, CRM, POS, memberships, digital contracts, messaging, live video, and marketing.',
  areaBlurb: 'Because K9os is fully cloud-based, dog trainers, board-and-train facilities, and kennels can run their entire business on it from any city, town, or rural area in Canada — with special density in these markets:',
  faq: [
    {
      q: 'Is K9os App the best dog training software for dog trainers in Canada?',
      a: 'Yes. K9os App is purpose-built for dog trainers and dog training companies across Canada — from solo trainers in Toronto and Vancouver to multi-trainer board-and-train facilities in Montreal, Calgary, and every province in between. It replaces standalone POS, scheduling, and CRM tools with one login for booking, payments, memberships, contracts, messaging, and marketing.',
    },
    {
      q: 'Does K9os work for board-and-train facilities in Canada?',
      a: 'Absolutely. K9os supports the full board-and-train workflow \u2014 intake forms, signed digital contracts, kennel run grids, daily notes, and live trainer video updates \u2014 ready for facilities in Toronto, Montreal, Vancouver, Calgary, and anywhere else in Canada. Be the FIRST board-and-train facility in your city to run on K9os.',
    },
    {
      q: 'Which currency and payment providers does K9os support in Canada?',
      a: 'K9os processes payments through Stripe, which supports CAD and every major card in Canada. Bookings, POS sales, and monthly memberships are all reconciled in one ledger, so Canadian dog training businesses can bill clients in their local currency with no add-on integrations.',
    },
    {
      q: 'Which Canadian cities and provinces does K9os serve?',
      a: 'K9os is a cloud-based platform, so any dog trainer with an internet connection in Canada can run their business on it — including Toronto, Montreal, Vancouver, Calgary, Ottawa, Edmonton, Mississauga, Winnipeg, Quebec City, Hamilton, and every other city and province from British Columbia to Newfoundland.',
    },
    {
      q: 'How do dog trainers in Canada get started with K9os?',
      a: 'Head to the Contact page or tap "Book a Demo" on any page to schedule a free walkthrough tailored to your Canadian dog training company.',
    },
  ],
};

export const WORLDWIDE = {
  slug: 'worldwide',
  title: 'Worldwide',
  countryCode: 'WW',
  headline: 'Dog Training Software Worldwide',
  subheadline: 'One App for International Dog Trainers, Board-and-Train Facilities & Kennels',
  eyebrow: 'International · Worldwide',
  areaLabel: 'international dog trainers worldwide',
  regions: [
    'United Kingdom', 'Australia', 'New Zealand', 'Ireland',
    'Germany', 'France', 'Netherlands', 'Spain',
    'South Africa', 'UAE', 'Singapore', 'Mexico',
  ],
  cities: [
    'London', 'Sydney', 'Auckland', 'Dublin',
    'Berlin', 'Paris', 'Amsterdam', 'Madrid',
    'Cape Town', 'Dubai',
  ],
  keywords: [
    'international dog training software',
    'dog training software worldwide',
    'dog trainer CRM worldwide',
    'dog trainer POS international',
    'global dog training business software',
    'dog training software UK',
    'dog training software Australia',
    'dog training software Europe',
    'all-in-one dog training system international',
  ],
  seoTitle: 'Dog Training Software Worldwide — K9os App for International Dog Trainers',
  seoDesc: 'K9os App is the all-in-one dog training software for international dog trainers, board-and-train facilities, and kennels worldwide — UK, Australia, New Zealand, Europe, and beyond. Online booking, CRM, POS, memberships, contracts, and marketing under one login.',
  intro: 'K9os App is the all-in-one dog training software for dog trainers and dog training companies worldwide. From London and Sydney to Berlin, Auckland, and beyond, international trainers use K9os to replace fragmented POS, scheduling, and CRM tools with one login for online booking, CRM, POS, memberships, digital contracts, messaging, live video, and marketing.',
  areaBlurb: 'K9os is fully cloud-based, so any dog trainer with an internet connection can run their business on it from anywhere in the world. K9os is ready to launch in these international markets \u2014 be the FIRST dog training business in your city or region to have your own K9os-powered app:',
  faq: [
    {
      q: 'Is K9os App available for dog trainers outside the USA and Canada?',
      a: 'Yes. K9os is a cloud-based dog training platform available to dog trainers worldwide — including the United Kingdom, Australia, New Zealand, Ireland, Germany, France, Netherlands, Spain, South Africa, the UAE, Singapore, Mexico, and beyond. Any trainer with an internet connection can sign up.',
    },
    {
      q: 'Does K9os support international currencies and payment methods?',
      a: 'K9os processes payments through Stripe, which supports over 135 currencies and every major international card. Bookings, POS sales, and monthly memberships are all reconciled in one ledger, so international dog training businesses can bill clients in their local currency without add-on integrations.',
    },
    {
      q: 'Is K9os the best dog training software for Australia / Europe?',
      a: 'K9os is designed to fit the way modern dog training businesses actually run — day training, board-and-train, group classes, private lessons, and boarding — so it works for a Sydney kennel, a Berlin K9 company, and an Auckland trainer alike. UK trainers have their own dedicated page. All you need is a browser and a Stripe account for your country.',
    },
    {
      q: 'Which international markets is K9os most active in?',
      a: 'Our strongest international concentration is in the United Kingdom, Australia, New Zealand, Ireland, Germany, France, Netherlands, Spain, South Africa, the UAE, Singapore, and Mexico — with new dog training companies onboarding from other countries every month.',
    },
    {
      q: 'How do international dog trainers get started with K9os?',
      a: 'Head to the Contact page or tap "Book a Demo" on any page to schedule a free walkthrough. We support demos across every time zone, tailored to your local dog training market.',
    },
  ],
};

export const UNITED_KINGDOM = {
  slug: 'uk',
  title: 'United Kingdom',
  countryCode: 'GB',
  headline: 'Dog Training Software in the United Kingdom',
  subheadline: 'One App for UK Dog Trainers, Board-and-Train Facilities & Kennels',
  eyebrow: 'United Kingdom · GB',
  areaLabel: 'the United Kingdom',
  // Map config — geoMercator projection focused on the British Isles.
  mapCenter: [-3, 54.5],
  mapScale: 2400,
  // UK region overlay — clickable European Electoral Regions (11 GB regions:
  // 9 English regions + Scotland + Wales). Northern Ireland is not in the
  // GB EER dataset so it stays as a non-clickable chip below the map for
  // now. Chip labels below MATCH the EER13NM values exactly so map clicks
  // resolve to the correct anchor via the auto-slugified id="region-<name>".
  subdivisionsGeo: 'https://cdn.jsdelivr.net/gh/martinjc/UK-GeoJSON@master/json/electoral/gb/topo_eer.json',
  subdivisionNameKey: 'EER13NM',
  mapCities: [
    { name: 'London',     coords: [-0.1276, 51.5074], color: '#ff5b5b', labelDy:  12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Manchester', coords: [-2.2426, 53.4808], color: '#ff5b5b', labelDy: -10, labelDx:  10, labelAnchor: 'start' },
    { name: 'Birmingham', coords: [-1.8904, 52.4862], color: '#ff5b5b', labelDy:  12, labelDx: -10, labelAnchor: 'end' },
    { name: 'Leeds',      coords: [-1.5491, 53.8008], color: '#ffcf40', labelDy: -10, labelDx:  10, labelAnchor: 'start' },
    { name: 'Glasgow',    coords: [-4.2518, 55.8642], color: '#ff5b5b', labelDy: -10, labelDx:  10, labelAnchor: 'start' },
    { name: 'Edinburgh',  coords: [-3.1883, 55.9533], color: '#ffcf40', labelDy:  12, labelDx:  10, labelAnchor: 'start' },
    { name: 'Bristol',    coords: [-2.5879, 51.4545], color: '#ffcf40', labelDy: -10, labelDx: -10, labelAnchor: 'end' },
    { name: 'Liverpool',  coords: [-2.9916, 53.4084], color: '#ffcf40', labelDy: -10, labelDx: -10, labelAnchor: 'end' },
    { name: 'Belfast',    coords: [-5.9301, 54.5973], color: '#ff5b5b', labelDy: -10, labelDx: -10, labelAnchor: 'end' },
    { name: 'Cardiff',    coords: [-3.1791, 51.4816], color: '#ffcf40', labelDy:  14, labelDx: -10, labelAnchor: 'end' },
  ],
  regions: [
    'London', 'South East', 'South West', 'Eastern',
    'East Midlands', 'West Midlands', 'North West', 'North East',
    'Yorkshire and The Humber', 'Scotland', 'Wales', 'Northern Ireland',
  ],
  cities: [
    'London', 'Manchester', 'Birmingham', 'Leeds', 'Glasgow',
    'Edinburgh', 'Bristol', 'Liverpool', 'Belfast', 'Cardiff',
  ],
  keywords: [
    'dog training software UK',
    'dog trainer software UK',
    'UK dog training business software',
    'dog trainer CRM UK',
    'dog trainer POS UK',
    'board and train software UK',
    'kennel management software UK',
    'dog training software United Kingdom',
    'dog training software London',
    'all-in-one dog training system UK',
  ],
  seoTitle: 'Dog Training Software UK — K9os App for United Kingdom Dog Trainers',
  seoDesc: 'K9os App is the all-in-one dog training software for UK dog trainers, board-and-train facilities, and kennels — London, Manchester, Birmingham, Leeds, Glasgow, Edinburgh and beyond. Online booking, CRM, POS, memberships, contracts, messaging, and marketing under one login.',
  intro: 'K9os App is the all-in-one dog training software built for dog trainers and dog training companies across the United Kingdom — from London and Manchester to Edinburgh, Cardiff, and Belfast. Replace fragmented POS, scheduling, and CRM tools with one login for online booking, CRM, POS, memberships, digital contracts, messaging, live video, and marketing.',
  areaBlurb: 'Because K9os is fully cloud-based, dog trainers, board-and-train facilities, and kennels can run their entire business on it from any city, town, or village in the UK — with special density in these markets:',
  faq: [
    {
      q: 'Is K9os App the best dog training software for UK dog trainers?',
      a: 'Yes. K9os App is purpose-built for dog trainers and dog training companies across the UK — from solo trainers in London and Manchester to multi-trainer board-and-train facilities in Birmingham, Edinburgh, and Glasgow. It replaces standalone POS, scheduling, and CRM tools with one login for booking, payments, memberships, contracts, messaging, and marketing.',
    },
    {
      q: 'Does K9os work for board-and-train facilities in the UK?',
      a: 'Absolutely. K9os supports the full board-and-train workflow \u2014 intake forms, signed digital contracts, kennel run grids, daily notes, and live trainer video updates \u2014 ready for facilities in London, Manchester, Leeds, Birmingham, and anywhere else in the United Kingdom. Be the FIRST board-and-train facility in your city to run on K9os.',
    },
    {
      q: 'Which currency and payment providers does K9os support in the UK?',
      a: 'K9os processes payments through Stripe, which supports GBP and every major card in the UK. Bookings, POS sales, and monthly memberships are all reconciled in one ledger, so UK dog training businesses can bill clients in pounds sterling with no add-on integrations.',
    },
    {
      q: 'Which UK cities and regions does K9os serve?',
      a: 'K9os is a cloud-based platform, so any dog trainer with an internet connection in the UK can run their business on it — including London, Manchester, Birmingham, Leeds, Glasgow, Edinburgh, Bristol, Liverpool, Belfast, Cardiff, and every other city and region across England, Scotland, Wales, and Northern Ireland.',
    },
    {
      q: 'How do dog trainers in the UK get started with K9os?',
      a: 'Head to the Contact page or tap "Book a Demo" on any page to schedule a free walkthrough tailored to your UK dog training company.',
    },
  ],
};

export const REGIONS = { canada: CANADA, uk: UNITED_KINGDOM, worldwide: WORLDWIDE };
export const findRegion = (slug) => REGIONS[slug];
